/* Personalized Monograms drawer — relocation + YMQ UI transforms.
   (experimental, Site Refresh 2026)

   YMQ injects its option boxes into the product form at runtime. This script:
   1. Moves every YMQ option box except real variant pickers into the drawer,
      and puts the needle-and-thread brand icon in the drawer heading.
   2. Converts the "Personalization" and "Add Design?" button groups into
      custom dropdowns. The original radio inputs stay in the DOM (hidden)
      and are driven by clicking their labels, so YMQ's price math,
      validation, and conditional show/hide keep working untouched. (Custom
      dropdowns, not <select>, so YMQ's own dropdown enhancer leaves them
      alone — it stamps "undefined" image slots on foreign selects.)
   3. Converts "Color - ..." image grids into side-by-side swatch dropdowns.
      The number of columns comes from YMQ's own max_s validation rule.
   All transforms are idempotent and re-run whenever YMQ re-renders. */
(function () {
  'use strict';

  /* Flag for the no-flash CSS in ap-monogram-drawer.css, which hides YMQ's
     relocatable boxes until they sit inside the drawer. Set before anything
     can fail; bail() clears it so a broken or stalled script degrades to
     the old inline layout instead of hiding personalization. */
  var root = document.documentElement;
  root.classList.add('ap-drawer-js');
  window.apMonogramDrawerLoaded = true; /* tells the snippet's inline failsafe we're here */
  function bail() { root.classList.remove('ap-drawer-js'); }

  var VARIANT_LABELS = ['size'];
  var DROPDOWN_LABELS = ['personalization', 'add design?'];
  var ORDINALS = ['Primary Color', 'Secondary Color', 'Third Color', 'Fourth Color'];
  var ICON_URL = 'https://cdn.shopify.com/s/files/1/0668/7784/9757/files/needle-n-thread.svg?v=1777990055';

  function boxLabel(box) {
    return (box.getAttribute('data-label') || '').trim().toLowerCase();
  }

  function labelFor(box, input) {
    return box.querySelector('label[for="' + CSS.escape(input.id) + '"]');
  }

  function clickInput(box, input) {
    var lab = labelFor(box, input);
    if (lab) { lab.click(); } else { input.click(); }
  }

  function priceSuffix(input) {
    var price = parseFloat(input.getAttribute('data-price') || '0');
    return price > 0 ? ' (+$' + price.toFixed(2) + ')' : '';
  }

  function swatchEl(img) {
    var sw = document.createElement('span');
    sw.className = 'ap-color-dd__swatch';
    if (img) sw.style.backgroundImage = 'url("' + img + '")';
    return sw;
  }

  /* Shared dropdown widget. options: [{key, text, img?}], onChoose(key).
     The open list is portaled to <body> with fixed positioning: the theme's
     scroll-in animation leaves a transform on every block wrapper, which
     makes each block its own stacking context — a popover kept in place
     would always paint underneath the next block ("Details"). */
  function makeDropdown(placeholder, options, onChoose) {
    var dd = document.createElement('details');
    dd.className = 'ap-color-dd';
    var summary = document.createElement('summary');
    dd.appendChild(summary);
    var list = document.createElement('div');
    list.className = 'ap-color-dd__list';
    dd.apList = list;
    options.forEach(function (o) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ap-color-dd__opt';
      if (o.img !== undefined) btn.appendChild(swatchEl(o.img));
      btn.appendChild(document.createTextNode(o.text));
      btn.addEventListener('click', function () {
        dd.removeAttribute('open');
        onChoose(o.key);
      });
      list.appendChild(btn);
    });
    dd.appendChild(list);

    function place() {
      var r = summary.getBoundingClientRect();
      list.style.position = 'fixed';
      list.style.top = (r.bottom + 4) + 'px';
      list.style.left = r.left + 'px';
      list.style.right = 'auto';
      list.style.width = r.width + 'px';
      list.style.zIndex = '9999';
    }

    function detach() {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      if (list.parentElement === document.body) dd.appendChild(list);
      list.removeAttribute('style');
    }

    dd.addEventListener('toggle', function () {
      if (dd.open) {
        place();
        document.body.appendChild(list);
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
      } else {
        detach();
      }
    });

    function setSummary(option) {
      summary.textContent = '';
      if (option && option.img !== undefined) summary.appendChild(swatchEl(option.img));
      summary.appendChild(document.createTextNode(option ? option.text : placeholder));
    }
    setSummary(null);

    return { el: dd, setSummary: setSummary };
  }

  /* --- Button groups → custom dropdown ----------------------------------- */
  function convertButtonsToDropdown(box) {
    if (DROPDOWN_LABELS.indexOf(boxLabel(box)) === -1) return;
    if (box.classList.contains('ap-ymq-dd-done')) return;
    var inputs = Array.prototype.slice.call(
      box.querySelectorAll('input.ymq-options-button-input')
    );
    if (!inputs.length) return;
    box.classList.add('ap-ymq-dd-done');

    var noneText = boxLabel(box) === 'add design?' ? 'No' : 'None';
    var options = [{ key: '', text: noneText }];
    inputs.forEach(function (input) {
      options.push({ key: input.id, text: input.value + priceSuffix(input) });
    });

    var syncing = false;
    var widget = makeDropdown(noneText, options, function (key) {
      syncing = true;
      if (key) {
        var input = document.getElementById(key);
        if (input && !input.checked) clickInput(box, input);
      } else {
        inputs.forEach(function (input) {
          if (input.checked) {
            // Toggle off via label first (YMQ's own path); force-uncheck if
            // the click re-checked the radio instead of clearing it.
            clickInput(box, input);
            if (input.checked) {
              input.checked = false;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }
      updateSummary();
      setTimeout(function () { syncing = false; }, 0);
    });

    function updateSummary() {
      var checked = box.querySelector('input.ymq-options-button-input:checked');
      var current = null;
      if (checked) {
        options.some(function (o) {
          if (o.key === checked.id) { current = o; return true; }
          return false;
        });
      }
      widget.setSummary(current);
    }
    updateSummary();

    var group = inputs[0].parentElement;
    group.classList.add('ap-ymq-grid-hidden');
    group.parentElement.insertBefore(widget.el, group);

    box.addEventListener('change', function () {
      if (!syncing) updateSummary();
    });
  }

  /* --- Color image grids → swatch dropdown columns ----------------------- */
  function convertColorBox(box) {
    if (boxLabel(box).indexOf('color -') !== 0) return;
    if (box.classList.contains('ap-color-dd-done')) return;
    var inputs = Array.prototype.slice.call(
      box.querySelectorAll('input.ymq-options-img-input')
    );
    var grid = box.querySelector('.spotlight-group');
    if (!inputs.length || !grid) return;
    box.classList.add('ap-color-dd-done');

    var validate = box.getAttribute('data-validate') || '';
    var maxMatch = validate.match(/max_s:(\d+)/);
    var isRadio = inputs[0].type === 'radio';
    var count = isRadio ? 1 : (maxMatch ? parseInt(maxMatch[1], 10) : 1);

    var colors = inputs.map(function (input) {
      var lab = labelFor(box, input);
      var bg = lab ? lab.style.backgroundImage || lab.style.background || '' : '';
      var m = bg.match(/url\(["']?([^"')]+)["']?\)/);
      return { key: input.id, text: input.value.trim(), img: m ? m[1] : '', input: input };
    });

    var row = document.createElement('div');
    row.className = 'ap-color-dd-row';
    var slots = [];

    function syncInputs() {
      if (isRadio) {
        var v = slots[0].value;
        if (v && !v.input.checked) clickInput(box, v.input);
        return;
      }
      var want = {};
      slots.forEach(function (slot) {
        if (slot.value) want[slot.value.key] = true;
      });
      // Uncheck first so YMQ's max-selection guard never trips.
      colors.forEach(function (c) {
        if (c.input.checked && !want[c.key]) clickInput(box, c.input);
      });
      colors.forEach(function (c) {
        if (!c.input.checked && want[c.key]) clickInput(box, c.input);
      });
    }

    function choose(index, color) {
      slots.forEach(function (slot, i) {
        if (i !== index && slot.value && slot.value.key === color.key) {
          slot.value = null;
          slot.widget.setSummary(null);
        }
      });
      slots[index].value = color;
      slots[index].widget.setSummary(color);
      syncInputs();
    }

    for (var i = 0; i < count; i++) {
      (function (index) {
        var placeholder = count > 1 ? (ORDINALS[index] || 'Color ' + (index + 1)) : 'Choose a color';
        var widget = makeDropdown(placeholder, colors, function (key) {
          var color = null;
          colors.some(function (c) {
            if (c.key === key) { color = c; return true; }
            return false;
          });
          if (color) choose(index, color);
        });
        slots.push({ widget: widget, value: null });
        row.appendChild(widget.el);
      })(i);
    }

    // Seed from anything YMQ pre-checked.
    colors
      .filter(function (c) { return c.input.checked; })
      .slice(0, count)
      .forEach(function (c, i) {
        slots[i].value = c;
        slots[i].widget.setSummary(c);
      });

    grid.classList.add('ap-ymq-grid-hidden');
    grid.parentElement.insertBefore(row, grid);
  }

  // Close any open dropdown when clicking elsewhere (the open list lives at
  // body level, so check it separately from the details element).
  document.addEventListener('click', function (evt) {
    document.querySelectorAll('.ap-color-dd[open]').forEach(function (dd) {
      var inList = dd.apList && dd.apList.contains(evt.target);
      if (!dd.contains(evt.target) && !inList) dd.removeAttribute('open');
    });
  });

  /* --- Relocation into the drawer ---------------------------------------- */
  function drawerParts() {
    var target = document.querySelector('[data-ap-monogram-target]');
    if (!target) return null;
    var accordion = target.closest('.product-info-accordion');
    if (accordion) {
      accordion.classList.add('ap-monogram-drawer');
      var summary = accordion.querySelector('summary.disclosure__title');
      if (summary && !summary.querySelector('.ap-monogram-drawer__icon')) {
        var icon = document.createElement('img');
        icon.src = ICON_URL;
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        icon.className = 'ap-monogram-drawer__icon';
        summary.insertBefore(icon, summary.firstChild);
      }
    }
    return { target: target, accordion: accordion };
  }

  function relocateAndConvert(parts) {
    var moved = false;
    document.querySelectorAll('.ymq-options-box').forEach(function (box) {
      if (VARIANT_LABELS.indexOf(boxLabel(box)) !== -1) return;
      if (!parts.target.contains(box)) {
        parts.target.appendChild(box);
        moved = true;
      }
      convertButtonsToDropdown(box);
      convertColorBox(box);
    });
    if (moved && parts.accordion) {
      parts.accordion.classList.add('is-populated');
    }
  }

  function hasStrayBoxes(parts) {
    return Array.prototype.some.call(document.querySelectorAll('.ymq-options-box'), function (box) {
      return VARIANT_LABELS.indexOf(boxLabel(box)) === -1 && !parts.target.contains(box);
    });
  }

  function init() {
    var parts;
    try {
      parts = drawerParts();
      if (!parts) { bail(); return; }
      relocateAndConvert(parts);
    } catch (e) {
      bail();
      throw e;
    }

    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        try { relocateAndConvert(parts); } catch (e) { bail(); throw e; }
      }, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    /* Failsafe: a relocatable box still outside the drawer a few seconds in
       means relocation isn't happening; show everything inline. Checked
       twice so a box caught mid-relocation doesn't trip it. */
    setTimeout(function () {
      if (!hasStrayBoxes(parts)) return;
      setTimeout(function () { if (hasStrayBoxes(parts)) bail(); }, 600);
    }, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
